import type { ProjectSolarPotential } from '@/api/queries/project/useGetProjectSolarPotential';
import type { SolarDesignDraftFieldsFragment } from '@/gql/graphql';
import type { SolarRoofViewProps } from '@payaca/components/solarRoofView/SolarRoofView';
import {
  solarDesignDraftStateSchema,
  type SolarDesignDraftState,
} from '@payaca/helpers/solarDesignDraft';
import { useMemo } from 'react';

type PreviewSource = Pick<
  ProjectSolarPotential,
  | 'sourceAnalysisId'
  | 'sourceAnalysisVersion'
  | 'geometry'
  | 'panels'
  | 'configurations'
  | 'defaultConfigurationIndex'
>;
type PreviewDraft = Pick<
  SolarDesignDraftFieldsFragment,
  'id' | 'state' | 'sourceAnalysisId' | 'sourceAnalysisVersion' | 'isStale'
>;

type LayoutPreview = Pick<
  SolarRoofViewProps,
  'faces' | 'packedPanels' | 'selectedPanelIndexes'
>;

const layoutPreview = (
  layout: SolarDesignDraftState['layout']
): LayoutPreview => {
  const { workingFaces, workingPanels, excludedFaceIds } = layout;
  const faceIndexes = new Map(workingFaces.map((face, i) => [face.id, i]));
  return {
    faces: workingFaces.map((face) => face.polygon),
    packedPanels: workingPanels.map((panel) => ({
      ...panel,
      faceIndex: faceIndexes.get(panel.faceId) ?? -1,
    })),
    selectedPanelIndexes: workingPanels.flatMap((panel, i) =>
      panel.included && !excludedFaceIds.includes(panel.faceId) ? [i] : []
    ),
  };
};

/** A hand-drawn design's saved working set; undefined when its state is unreadable. */
export const manualSolarPreview = (
  draftState: unknown
): LayoutPreview | undefined => {
  const parsed = solarDesignDraftStateSchema.safeParse(draftState);
  return parsed.success ? layoutPreview(parsed.data.layout) : undefined;
};

/** Match the designer's saved working set, or its initial selection. */
export const solarPreview = (
  source: PreviewSource,
  draft?: PreviewDraft | null
): Pick<
  SolarRoofViewProps,
  'faces' | 'packedPanels' | 'selectedPanelIndexes'
> => {
  const parsed = solarDesignDraftStateSchema.safeParse(draft?.state);
  const state =
    !draft?.isStale &&
    draft?.sourceAnalysisId === source.sourceAnalysisId &&
    draft?.sourceAnalysisVersion === source.sourceAnalysisVersion &&
    parsed.success
      ? parsed.data
      : undefined;

  if (state && (source.geometry || state.layout.manualMode)) {
    return layoutPreview(state.layout);
  }

  const packedPanels = source.geometry?.packedPanels;
  return {
    faces: source.geometry?.faces.map((face) => face.polygon),
    packedPanels,
    selectedPanelIndexes: packedPanels
      ? packedPanels.flatMap((panel, i) =>
          panel.onProperty !== false ? [i] : []
        )
      : (state?.selectedPanelIndexes ??
        source.panels
          .slice(
            0,
            source.configurations[source.defaultConfigurationIndex]
              ?.panelsCount ?? source.panels.length
          )
          .map((_, i) => i)),
  };
};

/** Signed imagery URLs rotate during polling; they do not change the layout. */
export const useSolarPreview = (
  source: ProjectSolarPotential | null | undefined,
  draft: SolarDesignDraftFieldsFragment | null | undefined
) => {
  const {
    sourceAnalysisId,
    sourceAnalysisVersion,
    geometry,
    panels,
    configurations,
    defaultConfigurationIndex,
  } = source ?? {};
  const {
    id,
    state,
    sourceAnalysisId: draftSourceId,
    sourceAnalysisVersion: draftSourceVersion,
    isStale,
  } = draft ?? {};
  return useMemo(() => {
    if (
      !sourceAnalysisId ||
      sourceAnalysisVersion == null ||
      !panels ||
      !configurations ||
      defaultConfigurationIndex == null
    )
      return undefined;
    return solarPreview(
      {
        sourceAnalysisId,
        sourceAnalysisVersion,
        geometry,
        panels,
        configurations,
        defaultConfigurationIndex,
      },
      id && draftSourceId && draftSourceVersion != null && isStale != null
        ? {
            id,
            state,
            sourceAnalysisId: draftSourceId,
            sourceAnalysisVersion: draftSourceVersion,
            isStale,
          }
        : undefined
    );
  }, [
    sourceAnalysisId,
    sourceAnalysisVersion,
    geometry,
    panels,
    configurations,
    defaultConfigurationIndex,
    id,
    state,
    draftSourceId,
    draftSourceVersion,
    isStale,
  ]);
};
